import debounce from 'lodash/debounce';

// The server already throttles signals per org; a short debounce keeps retry rounds visible. Never in a hidden tab.
export const PROPAGATION_REFRESH_WAIT_MS = 500;
export const PROPAGATION_REFRESH_MAX_WAIT_MS = 2000;

/**
 * Coalesced propagation-snapshot refresh; a hidden tab defers it until it becomes visible.
 * @param {Object} p
 * @param {() => void} p.refresh - dispatches the propagation snapshot fetch
 * @param {() => boolean} [p.isHidden] - true while the tab is hidden; defaults to document.hidden
 * @returns {{ onChanged: () => void, onVisibilityChange: () => void, cancel: () => void }}
 */
export function createPropagationRefresher({
	refresh,
	isHidden = () => typeof document !== 'undefined' && document.hidden
}) {
	let pendingWhileHidden = false;

	const refreshIfVisible = () => {
		if (isHidden()) {
			pendingWhileHidden = true;
			return;
		}
		pendingWhileHidden = false;
		refresh();
	};

	const debouncedRefresh = debounce(refreshIfVisible, PROPAGATION_REFRESH_WAIT_MS, {
		maxWait: PROPAGATION_REFRESH_MAX_WAIT_MS
	});

	return {
		onChanged: () => debouncedRefresh(),
		onVisibilityChange: () => {
			if (!isHidden() && pendingWhileHidden) {
				pendingWhileHidden = false;
				refresh();
			}
		},
		cancel: () => {
			debouncedRefresh.cancel();
			pendingWhileHidden = false;
		}
	};
}
